import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { pressReleaseService } from '@/lib/api/services/press-release'
import { pressReleaseCommentService } from '@/lib/api/services/press-release-comment'
import type { PressReleaseFilterParams, PaginationParams, CreatePressRelease, UpdatePressRelease, ApiResponse, PaginatedResponse, PressRelease, PressReleaseStats, PressReleaseStatsResponse, PressReleaseCategory } from '@/lib/api/types'
import { showToast } from '@/lib/toast'

// Query keys for press release management
export const pressReleaseKeys = {
  all: ['press-release'] as const,
  pressReleases: () => [...pressReleaseKeys.all, 'press-releases'] as const,
  pressRelease: (id: string) => [...pressReleaseKeys.pressReleases(), id] as const,
  pressReleasesPaginated: (params: PaginationParams, filters: PressReleaseFilterParams) =>
    [...pressReleaseKeys.pressReleases(), 'paginated', params, filters] as const,
  pressReleasesAll: () => [...pressReleaseKeys.pressReleases(), 'all'] as const,
  pressReleasesPublic: () => [...pressReleaseKeys.pressReleases(), 'public'] as const,
  pressReleasesSearch: (query: string) => [...pressReleaseKeys.pressReleases(), 'search', query] as const,
  stats: () => [...pressReleaseKeys.all, 'stats'] as const,
  tags: () => [...pressReleaseKeys.all, 'tags'] as const,
  categories: () => [...pressReleaseKeys.all, 'categories'] as const,
  comments: (pressReleaseId: string) => [...pressReleaseKeys.all, 'comments', pressReleaseId] as const,
}


export const usePressReleases = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: PressReleaseFilterParams = {}
) => {
  return useQuery({
    queryKey: pressReleaseKeys.pressReleasesPaginated(params, filters),
    queryFn: () => pressReleaseService.getPressReleases(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData
  })
}


export const useAllPressReleases = () => {
  return useQuery({
    queryKey: pressReleaseKeys.pressReleasesAll(),
    queryFn: () => pressReleaseService.getAllPressReleases(),
    staleTime: 5 * 60 * 1000,
    enabled: false
  })
}


export const usePublicPressReleases = () => {
  return useQuery({
    queryKey: pressReleaseKeys.pressReleasesPublic(),
    queryFn: () => pressReleaseService.getPublicPressReleases(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: false
  })
}


export const usePressRelease = (pressReleaseId: string) => {
  return useQuery({
    queryKey: pressReleaseKeys.pressRelease(pressReleaseId),
    queryFn: () => pressReleaseService.getPressReleaseById(pressReleaseId),
    enabled: !!pressReleaseId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}


export const useSearchPressReleases = (
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...pressReleaseKeys.pressReleasesSearch(searchBy || ''), params],
    queryFn: () => pressReleaseService.searchPressReleases(params, searchBy),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  })
}


export const useInfinitePressReleases = (baseFilters: PressReleaseFilterParams = {}) => {
  return useInfiniteQuery({
    queryKey: [...pressReleaseKeys.pressReleases(), 'infinite', baseFilters],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      pressReleaseService.getPressReleases({ page: pageParam, size: 10 }, baseFilters),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<PaginatedResponse<PressRelease>>, allPages: ApiResponse<PaginatedResponse<PressRelease>>[]) => {
      if (!lastPage.success || !lastPage.data) return undefined

      const currentPage = allPages.length
      const totalPages = lastPage.data.totalPage

      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes (formerly cacheTime)
    placeholderData: (previousData) => previousData, // Keep previous data while refetching
    refetchOnWindowFocus: false, // Don't refetch on window focus to preserve cache
    refetchOnMount: false // Don't refetch on mount if data is fresh
  })
}


export const usePressReleaseStats = () => {
  return useQuery<PressReleaseStatsResponse, Error, PressReleaseStats>({
    queryKey: pressReleaseKeys.stats(),
    queryFn: () => pressReleaseService.getPressReleaseStats(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: (previousData) => previousData
  })
}


export const useArticleTags = () => {
  return useQuery({
    queryKey: pressReleaseKeys.tags(),
    queryFn: () => pressReleaseService.getArticleTags(),
    staleTime: 30 * 60 * 1000
  })
}

export const usePressReleaseCategories = () => {
  return useQuery<ApiResponse<PressReleaseCategory[]>, Error>({
    queryKey: pressReleaseKeys.categories(),
    queryFn: () => pressReleaseService.getPressReleaseCategories(),
  })
}


export const useCreatePressRelease = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePressRelease) => pressReleaseService.createPressRelease(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: pressReleaseKeys.pressReleases(),
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: pressReleaseKeys.stats(),
          refetchType: 'all',
        }),
      ])
    },
    onError: (error) => {
      console.error('Press release creation failed:', error)
    }
  })
}


export const useUpdatePressRelease = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pressReleaseId, data }: { pressReleaseId: string; data: UpdatePressRelease }) =>
      pressReleaseService.updatePressRelease(pressReleaseId, data),
    onSuccess: (_response, { pressReleaseId }) => {

      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressRelease(pressReleaseId) })


      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressReleases(), refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.stats(), refetchType: 'all' })
    },
    onError: (error) => {
      console.error('Press release update failed:', error)
    }
  })
}


export const useUpdatePressReleaseStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pressReleaseId, status }: { pressReleaseId: string; status: 'DRAFT' | 'PUBLISH' }) =>
      pressReleaseService.updatePressReleaseStatus(pressReleaseId, status),
    onSuccess: (response, { pressReleaseId }) => {

      if (response.success && response.data) {
        queryClient.setQueryData(pressReleaseKeys.pressRelease(pressReleaseId), response.data)
      }


      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressReleases(), refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.stats(), refetchType: 'all' })
    },
    onError: (error) => {
      console.error('Press release status update failed:', error)
    }
  })
}


export const useDeletePressRelease = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (pressReleaseId: string) => pressReleaseService.deletePressRelease(pressReleaseId),
    onSuccess: (_, pressReleaseId) => {

      queryClient.removeQueries({ queryKey: pressReleaseKeys.pressRelease(pressReleaseId) })


      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressReleases() })
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.stats() })
    },
    onError: (error) => {
      console.error('Press release deletion failed:', error)
    }
  })
}

/**
 * Hook to fetch press release comments with replies
 */
export const usePressReleaseCommentsWithReplies = (pressReleaseId: string) => {
  return useQuery({
    queryKey: pressReleaseKeys.comments(pressReleaseId || ''),
    queryFn: () => pressReleaseCommentService.getCommentsWithReplies(pressReleaseId),
    enabled: !!pressReleaseId,
    staleTime: 1 * 60 * 1000 // 1 minute
  })
}

/**
 * Hook for infinite scroll press release comments (legacy - kept for compatibility)
 */
export const useInfinitePressReleaseComments = (pressReleaseId: string) => {
  return useInfiniteQuery({
    queryKey: pressReleaseKeys.comments(pressReleaseId || ''),
    queryFn: () =>
      pressReleaseCommentService.getCommentsWithReplies(pressReleaseId),
    enabled: !!pressReleaseId,
    initialPageParam: 1,
    getNextPageParam: () => undefined, // No pagination for now since API returns all comments
    staleTime: 1 * 60 * 1000 // 1 minute
  })
}

/**
 * Hook to delete a single reply
 */
export const useDeletePressReleaseReply = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (replyId: string) => pressReleaseCommentService.deleteReply(replyId),
    onSuccess: () => {
      // Invalidate all comments queries to refetch
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.all })
    },
    onError: (error) => {
      console.error('Reply deletion failed:', error)
    }
  })
}

/**
 * Hook to delete a comment and all its replies
 */
export const useDeletePressReleaseComment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (commentId: string) => pressReleaseCommentService.deleteCommentWithReplies(commentId),
    onSuccess: () => {
      // Invalidate all comments queries to refetch
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.all })
    },
    onError: (error) => {
      console.error('Comment deletion failed:', error)
    }
  })
}

/**
 * Hook to add a reply to a comment
 */
export const useReplyToPressReleaseComment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId, reply }: { commentId: string; reply: string }) =>
      pressReleaseCommentService.addReply(commentId, reply),
    onSuccess: () => {
      // Invalidate all comments queries to refetch
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.all })
    },
    onError: (error) => {
      console.error('Reply addition failed:', error)
    }
  })
}

/**
 * Hook to fetch home page highlights
 */
export const useHomePageHighlights = (type: string = 'HOME_PAGE_HERO') => {
  return useQuery({
    queryKey: [...pressReleaseKeys.all, 'highlights', type],
    queryFn: () => pressReleaseService.getHomePageHighlights(type),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to update home page highlights
 */
export const useUpdateHomePageHighlights = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({pressReleaseIds, pressReleaseHighlightType, landingPagePositions}: {pressReleaseIds: string[], pressReleaseHighlightType: string, landingPagePositions: Record<string, number>}) =>
      pressReleaseService.updateHomePageHighlights(pressReleaseIds, pressReleaseHighlightType, landingPagePositions),
    onSuccess: () => {
      // Invalidate highlights query to refetch
      queryClient.invalidateQueries({ queryKey: [...pressReleaseKeys.all, 'highlights'] })
      showToast.success('Press release highlights updated successfully')
    },
    onError: (error) => {
      console.error('Failed to update highlights:', error)
      showToast.error('Failed to update press release highlights')
    },
  })
}
